import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import FooterSection from "@/components/FooterSection";

const PrivacyPolicy = () => (
  <>
    <Helmet>
      <title>Privacy Policy | Zenith Solutions</title>
      <meta name="description" content="Zenith Solutions privacy policy. Learn how we collect, use, and protect your personal information including phone numbers and SMS communications." />
    </Helmet>
    <Navbar />
    <div className="min-h-screen bg-background">
      <div className="container mx-auto max-w-3xl px-4 pt-28 pb-20">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 mb-8">
          <ArrowLeft className="h-4 w-4" /> Back to Home
        </Link>

        <h1 className="text-3xl md:text-4xl font-black text-foreground mb-2">Privacy Policy</h1>
        <p className="text-muted-foreground mb-2"><strong className="text-foreground">Zenith Solutions LLC</strong></p>
        <p className="text-muted-foreground mb-10">Last updated: March 17, 2026</p>

        <div className="prose prose-sm max-w-none text-foreground/90 space-y-8">
          <section>
            <h2 className="text-xl font-bold text-foreground">1. Information We Collect</h2>
            <p className="text-muted-foreground leading-relaxed">
              When you use the Zenith Solutions website or contact us, we may collect the following personal information:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1">
              <li>Name, email address, and <strong>phone number</strong> (when you submit a contact form, request a quote, or opt in to text messaging)</li>
              <li>Information about your project or service needs</li>
              <li>SMS opt-in consent status and preferences</li>
              <li>Device and browser information collected automatically through cookies and analytics</li>
              <li>IP address and general location data</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-foreground">2. How We Use Your Information</h2>
            <p className="text-muted-foreground leading-relaxed">We use the information we collect to:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1">
              <li>Respond to your inquiries and provide estimates</li>
              <li>Communicate with you about our services, including via <strong>SMS/text messages</strong> if you have opted in</li>
              <li>Send appointment reminders, project updates, and follow-up communications via text message</li>
              <li>Improve our website and customer experience</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-foreground">3. Text Messaging (SMS) Communications</h2>
            <p className="text-muted-foreground leading-relaxed">
              If you opt in to receive text messages from Zenith Solutions, you agree to the following:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1">
              <li><strong>Sender:</strong> Text messages will be sent by or on behalf of Zenith Solutions LLC.</li>
              <li><strong>Message frequency:</strong> Message frequency varies. You may receive recurring messages related to your inquiry, estimate, appointment, or project status.</li>
              <li><strong>Message & data rates:</strong> Standard message and data rates may apply depending on your mobile carrier and plan.</li>
              <li><strong>Opt-out:</strong> You can opt out of text messages at any time by replying <strong>STOP</strong> to any message you receive from us. After opting out, you will receive a one-time confirmation message and no further texts.</li>
              <li><strong>Help:</strong> For help, reply <strong>HELP</strong> to any message or contact us at (505) 604-8536 or info@zenithsolutionsnm.com.</li>
            </ul>
            <div className="bg-muted/50 border border-border rounded-lg p-4 mt-4">
              <p className="text-sm text-foreground font-semibold mb-1">Important Disclosure</p>
              <p className="text-sm text-muted-foreground">
                All the above categories exclude text messaging originator opt-in data and consent; this information will not be shared with any third parties for marketing purposes.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-foreground">4. Information Sharing</h2>
            <p className="text-muted-foreground leading-relaxed">
              We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1">
              <li>With service providers who assist us in operating our website and business (e.g., CRM platforms, email/SMS delivery services)</li>
              <li>When required by law or to protect our rights</li>
              <li>With your explicit consent</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-2">
              <strong>We do not share your text messaging opt-in data or consent with any third parties for their marketing or promotional purposes.</strong>
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-foreground">5. Data Security & Retention</h2>
            <p className="text-muted-foreground leading-relaxed">
              We implement appropriate security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. This includes encryption of data in transit and secure storage of your information.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-2">
              We retain your personal information only for as long as necessary to fulfill the purposes described in this policy, comply with legal obligations, resolve disputes, and enforce our agreements. Contact form submissions and SMS consent records are retained for the duration of our business relationship and as required by applicable regulations.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-foreground">6. Cookies</h2>
            <p className="text-muted-foreground leading-relaxed">
              Our website may use cookies and similar technologies to enhance your browsing experience and analyze website traffic. You can control cookie preferences through your browser settings.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-foreground">7. Your Rights</h2>
            <p className="text-muted-foreground leading-relaxed">You have the right to:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1">
              <li>Request access to your personal information</li>
              <li>Request correction or deletion of your data</li>
              <li>Opt out of marketing communications (including SMS — reply STOP at any time)</li>
              <li>Withdraw consent for text messaging at any time</li>
              <li>Lodge a complaint with a supervisory authority</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-foreground">8. Changes to This Policy</h2>
            <p className="text-muted-foreground leading-relaxed">
              We reserve the right to update this Privacy Policy at any time. Changes will be effective immediately upon posting to this page. We encourage you to review this policy periodically.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-foreground">9. Contact Us</h2>
            <p className="text-muted-foreground leading-relaxed">
              If you have questions about this Privacy Policy, our data practices, or your SMS consent, please contact us at:
            </p>
            <p className="text-muted-foreground">
              <strong className="text-foreground">Zenith Solutions LLC</strong><br />
              Albuquerque, NM<br />
              Phone: (505) 604-8536<br />
              Email: info@zenithsolutionsnm.com
            </p>
          </section>
        </div>
      </div>
    </div>
    <FooterSection />
  </>
);

export default PrivacyPolicy;
